import { useRef, useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  title: "",
  description: "",
  image: "",
  canonical: "",
  noindex: false,
  keywords: "",
};

/**
 * Controlled SEO sub-form. Parent owns the seo object and passes
 * onChange to receive partial updates.
 *
 * Used inside the Product editor, Article editor and the static-page CMS.
 */
export default function SEOFields({ value, onChange, testIdPrefix = "seo", showCanonical = true, showNoindex = true }) {
  const raw = { ...EMPTY, ...(value || {}) };
  // Coalesce null → "" so controlled inputs stay strings (backend returns nulls
  // for empty SeoBlock fields).
  const v = {
    title: raw.title ?? "",
    description: raw.description ?? "",
    image: raw.image ?? "",
    canonical: raw.canonical ?? "",
    noindex: !!raw.noindex,
    keywords: raw.keywords ?? "",
  };
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const set = (k, val) => onChange({ ...v, [k]: val });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/site/upload-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const json = await r.json();
      if (!r.ok) throw new Error(json.detail || "Upload échoué");
      const url = json.url.startsWith("http") ? json.url : `${BACKEND_URL}${json.url}`;
      set("image", url);
      toast.success("Image OG téléchargée");
    } catch (err) {
      toast.error(err.message || "Erreur upload");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-fields`}>
      <div>
        <Label>Titre SEO <span className="text-xs text-[#4B634B]">({v.title.length}/80)</span></Label>
        <Input
          data-testid={`${testIdPrefix}-title`}
          value={v.title}
          maxLength={80}
          onChange={(e) => set("title", e.target.value)}
          placeholder="Vide ⇒ utilise le nom de l'élément"
        />
      </div>

      <div>
        <Label>Description SEO <span className="text-xs text-[#4B634B]">({v.description.length}/320)</span></Label>
        <Textarea
          data-testid={`${testIdPrefix}-description`}
          value={v.description}
          maxLength={320}
          rows={3}
          onChange={(e) => set("description", e.target.value)}
          placeholder="Affiché dans Google et lors d'un partage sur les réseaux sociaux"
        />
      </div>

      <div>
        <Label>Image Open Graph (URL ou upload)</Label>
        <div className="flex flex-wrap items-center gap-2 mt-1">
          <Input
            data-testid={`${testIdPrefix}-image`}
            value={v.image}
            onChange={(e) => set("image", e.target.value)}
            placeholder="https://… ou /api/files/…"
          />
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            onChange={onFile}
            className="hidden"
            data-testid={`${testIdPrefix}-file-input`}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid={`${testIdPrefix}-upload-btn`}
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Uploader
          </Button>
        </div>
        <p className="text-xs text-[#4B634B] mt-1">Format recommandé : 1200×630 px (ratio Open Graph).</p>
      </div>

      <div>
        <Label>Mots-clés (séparés par des virgules)</Label>
        <Input
          data-testid={`${testIdPrefix}-keywords`}
          value={v.keywords}
          onChange={(e) => set("keywords", e.target.value)}
          placeholder="herbalife, nutrition, shake protéiné"
        />
      </div>

      {showCanonical && (
        <div>
          <Label>URL canonique (override)</Label>
          <Input
            data-testid={`${testIdPrefix}-canonical`}
            value={v.canonical}
            onChange={(e) => set("canonical", e.target.value)}
            placeholder="Laissez vide pour utiliser l'URL actuelle"
          />
        </div>
      )}

      {showNoindex && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-[#F9FAF6] border border-[#E5EAE5]">
          <div>
            <Label className="block">Empêcher l'indexation (noindex)</Label>
            <p className="text-xs text-[#4B634B]">Si activé, Google et les autres moteurs ignoreront cette page.</p>
          </div>
          <Switch
            checked={!!v.noindex}
            onCheckedChange={(c) => set("noindex", c)}
            data-testid={`${testIdPrefix}-noindex`}
          />
        </div>
      )}
    </div>
  );
}
